import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { notify } from "../../utils/notify";
import { Submission, SubmissionStatus } from "../../types/cms";
import { primaryButton, secondaryButton } from "../admin/ui";

const LABEL: Record<SubmissionStatus, string> = {
  new: "À traiter",
  accepted: "Acceptée",
  rejected: "Refusée",
};

export default function SubmissionsView({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<SubmissionStatus>("new");

  useEffect(() => {
    getDocs(query(collection(db, "submissions"), orderBy("createdAt", "desc")))
      .then((snap) => {
        const list = snap.docs.map((d) => ({ ...(d.data() as Submission), id: d.id }));
        setItems(list);
        onCountChange?.(list.filter((s) => s.status === "new").length);
      })
      .catch((err) => {
        console.error(err);
        notify({ isSuccess: false, message: "Chargement des soumissions impossible", info: err.message });
      })
      .finally(() => setLoading(false));
  }, [onCountChange]);

  const setStatus = async (item: Submission, status: SubmissionStatus) => {
    await updateDoc(doc(db, "submissions", item.id), { status });
    setItems((prev) => {
      const next = prev.map((s) => (s.id === item.id ? { ...s, status } : s));
      onCountChange?.(next.filter((s) => s.status === "new").length);
      return next;
    });
  };

  const accept = async (item: Submission) => {
    await setStatus(item, "accepted");
    navigate(`/admin/video/new?url=${encodeURIComponent(item.url)}`);
  };

  const visible = items.filter((s) => s.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 p-1 bg-neutral-200/70 rounded-md">
          {(Object.keys(LABEL) as SubmissionStatus[]).map((key) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 text-sm rounded ${filter === key ? "bg-white shadow-sm font-medium" : "text-neutral-600"}`}
            >
              {LABEL[key]} <span className="tabular-nums text-neutral-500">{items.filter((s) => s.status === key).length}</span>
            </button>
          ))}
        </div>
        <p className="text-sm text-neutral-500">
          Formulaire public : <a className="underline" href="/submit" target="_blank" rel="noreferrer">/submit</a>
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500 animate-pulse">Chargement…</p>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-lg px-6 py-10 text-center text-sm text-neutral-500">
          {filter === "new" ? "Aucune soumission en attente." : "Rien ici pour le moment."}
        </div>
      ) : (
        <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg bg-white">
          {visible.map((item) => (
            <li key={item.id} className="p-4 flex flex-col md:flex-row md:items-start gap-3">
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <a href={item.url} target="_blank" rel="noreferrer" className="text-sm font-medium underline break-all">
                  {item.title || item.url}
                </a>
                <p className="text-sm text-neutral-600">
                  {[item.brandName && `Marque : ${item.brandName}`, item.studioName && `Studio : ${item.studioName}`]
                    .filter(Boolean)
                    .join(" · ") || "Marque et studio non précisés"}
                </p>
                {item.message && <p className="text-sm text-neutral-800 whitespace-pre-line">{item.message}</p>}
                <p className="text-xs text-neutral-500">
                  {item.contactEmail} · {item.createdAt?.toDate?.().toLocaleDateString("fr-FR")}
                </p>
              </div>
              {item.status === "new" && (
                <div className="flex gap-2 shrink-0">
                  <button className={secondaryButton} onClick={() => setStatus(item, "rejected")}>
                    Refuser
                  </button>
                  <button className={primaryButton} onClick={() => accept(item)}>
                    Créer la fiche
                  </button>
                </div>
              )}
              {item.status !== "new" && (
                <button className="text-sm underline text-neutral-600 shrink-0" onClick={() => setStatus(item, "new")}>
                  Remettre à traiter
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
